import { supabase } from "@/integrations/supabase/client";
import type { Json, TablesInsert } from "@/integrations/supabase/types";
import type {
  ContentFrameworkColumn, ContentFrameworkPrinciple, ContentFrameworkTopic,
  ContentBriefDraft, ContentBriefPublication,
} from "./types";
import { getFriendlyMessage, briefPublicationsTable } from "./_shared";
import { normalizeColorKey } from "./palette";
import { fetchComposerSnapshot } from "./snapshot";
import { createSource } from "./pieces";

import {
  contentDestinationFormatLabels as _contentDestinationFormatLabels,
  contentDestinationFormatExtension as _contentDestinationFormatExtension,
  getDefaultComposerTemplate,
} from "@/lib/composer/composerTemplateData";

export const contentDestinationFormatLabels = _contentDestinationFormatLabels;
export const contentDestinationFormatExtension = _contentDestinationFormatExtension;
export { getDefaultComposerTemplate };

export const seedComposerTemplate = async (workspaceId: string) => {
  const existing = await fetchComposerSnapshot(workspaceId);
  if (existing.frameworks.length || existing.avatars.length || existing.voiceProfile) return existing;

  const template = getDefaultComposerTemplate();
  const { data: framework, error: frameworkError } = await supabase
    .from("content_frameworks")
    .insert({
      workspace_id: workspaceId,
      name: template.framework.name,
      description: template.framework.description,
      column_count: template.framework.column_count,
      template_key: template.framework.template_key,
    })
    .select("*")
    .single();

  if (frameworkError) throw new Error(getFriendlyMessage(frameworkError, "Failed to create the starter framework"));

  const insertedColumns: ContentFrameworkColumn[] = [];
  const insertedPrinciples: ContentFrameworkPrinciple[] = [];
  const insertedTopics: ContentFrameworkTopic[] = [];

  for (const column of template.columns) {
    const { data: insertedColumn, error: columnError } = await supabase
      .from("content_framework_columns")
      .insert({
        workspace_id: workspaceId,
        framework_id: framework.id,
        label: column.label,
        description: column.description,
        position: column.position,
        color_key: column.color_key,
        progression_key_label: column.progression_key_label,
        progression_key_description: column.progression_key_description,
      })
      .select("*")
      .single();

    if (columnError) throw new Error(getFriendlyMessage(columnError, "Failed to create the starter columns"));
    insertedColumns.push(insertedColumn as ContentFrameworkColumn);

    const principlePayload = column.principles.map((label, index) => ({
      workspace_id: workspaceId,
      framework_column_id: insertedColumn.id,
      label,
      position: index,
    }));

    if (principlePayload.length) {
      const { data, error } = await supabase.from("content_framework_principles").insert(principlePayload).select("*");
      if (error) throw new Error(getFriendlyMessage(error, "Failed to create the starter principles"));
      insertedPrinciples.push(...((data ?? []) as ContentFrameworkPrinciple[]));
    }

    const topicPayload = column.topics.map((label, index) => ({
      workspace_id: workspaceId,
      framework_column_id: insertedColumn.id,
      label,
      position: index,
    }));

    if (topicPayload.length) {
      const { data, error } = await supabase.from("content_framework_topics").insert(topicPayload).select("*");
      if (error) throw new Error(getFriendlyMessage(error, "Failed to create the starter topics"));
      insertedTopics.push(...((data ?? []) as ContentFrameworkTopic[]));
    }
  }

  const seedVoice = {
    core_identity: "The Practical Mystic — aspirational and poetic, yet grounded in mechanical reality.",
    phrase_preferences: "Use carefully grounded reframings, precise conceptual language, and occasional distillation lines for emphasis.",
    avoid_list: "Avoid generic internet-marketing hype, shallow certainty, and language detached from lived experience.",
  };
  const { data: insertedVoice, error: voiceError } = await supabase
    .from("content_voice_profiles")
    .insert({
      workspace_id: workspaceId,
      title: "Master voice profile",
      is_primary: true,
    } as TablesInsert<"content_voice_profiles">)
    .select("id")
    .single();
  if (voiceError) throw new Error(getFriendlyMessage(voiceError, "Failed to create the starter voice profile"));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RPC signature narrows after migration regenerates types
  await (supabase as any).rpc("set_voice_profile_text", {
    _voice_id: (insertedVoice as { id: string }).id,
    _avoid_list: seedVoice.avoid_list,
    _phrase_preferences: seedVoice.phrase_preferences,
    _core_identity: seedVoice.core_identity,
  });

  if (template.avatars.length) {
    const avatarPayload: TablesInsert<"content_avatars">[] = template.avatars.map((avatar, index) => ({
      workspace_id: workspaceId,
      name: avatar.name,
      experience_level: avatar.experienceLevel,
      pain_points: avatar.painPoints,
      desires: avatar.desires,
      tone_guidance: avatar.toneGuidance,
      vocabulary_assumptions: avatar.vocabularyAssumptions,
      messaging_posture: avatar.messagingPosture,
      sort_order: index,
    }));
    const { data: insertedAvatars, error } = await supabase
      .from("content_avatars")
      .insert(avatarPayload)
      .select("id");
    if (error) throw new Error(getFriendlyMessage(error, "Failed to create the starter avatars"));
    const avatarIds = (insertedAvatars ?? []) as Array<{ id: string }>;
    await Promise.all(
      avatarIds.map((row, index) =>
        supabase.rpc("set_content_avatar_text", {
          _avatar_id: row.id,
          _summary: template.avatars[index]?.summary ?? null,
          _notes: null,
        }),
      ),
    );
  }

  const columnLookup = new Map(insertedColumns.map((column) => [column.id, column]));
  const chipPayload: TablesInsert<"content_chips">[] = [
    { workspace_id: workspaceId, chip_type: "source_bucket", label: "Primary", color_key: "plum", source_bucket: "primary", metadata: { seeded: true } },
    { workspace_id: workspaceId, chip_type: "source_bucket", label: "Secondary", color_key: "amber", source_bucket: "secondary", metadata: { seeded: true } },
    { workspace_id: workspaceId, chip_type: "source_bucket", label: "Supplemental", color_key: "silver", source_bucket: "supplemental", metadata: { seeded: true } },
    ...insertedColumns.flatMap<TablesInsert<"content_chips">>((column) => [
      {
        workspace_id: workspaceId,
        chip_type: "column",
        label: column.label,
        description: column.description,
        color_key: normalizeColorKey(column.color_key),
        linked_column_id: column.id,
        metadata: { framework_id: framework.id, seeded: true },
      },
      ...(column.progression_key_label
        ? [
            {
              workspace_id: workspaceId,
              chip_type: "progression_key",
              label: column.progression_key_label,
              description: column.progression_key_description,
              color_key: normalizeColorKey(column.color_key),
              linked_column_id: column.id,
              metadata: { framework_id: framework.id, seeded: true },
            } satisfies TablesInsert<"content_chips">,
          ]
        : []),
    ]),
    ...insertedPrinciples.map<TablesInsert<"content_chips">>((principle) => ({
      workspace_id: workspaceId,
      chip_type: "core_principle",
      label: principle.label,
      description: principle.description,
      color_key: normalizeColorKey(columnLookup.get(principle.framework_column_id)?.color_key),
      linked_principle_id: principle.id,
      linked_column_id: principle.framework_column_id,
      metadata: { seeded: true },
    })),
    ...insertedTopics.map<TablesInsert<"content_chips">>((topic) => ({
      workspace_id: workspaceId,
      chip_type: topic.parent_topic_id ? "subtopic" : "topic",
      label: topic.label,
      description: topic.description,
      color_key: normalizeColorKey(columnLookup.get(topic.framework_column_id)?.color_key),
      linked_topic_id: topic.id,
      linked_column_id: topic.framework_column_id,
      metadata: { seeded: true, parent_topic_id: topic.parent_topic_id },
    })),
  ];

  const { error: chipsError } = await supabase.from("content_chips").insert(chipPayload);
  if (chipsError) throw new Error(getFriendlyMessage(chipsError, "Failed to create the starter chip set"));

  return fetchComposerSnapshot(workspaceId);
};

// ─── Publishing workflow state ────────────────────────────────────────────
// Tracked inside content_piece_publications.context_snapshot.workflow so we
// don't need a schema migration. Default (missing) state is "outbox".

export type PublicationWorkflowState = "outbox" | "archived" | "promoted" | "sent";

export type PublicationWorkflow = {
  state: PublicationWorkflowState;
  resolved_at?: string | null;
  promoted_source_id?: string | null;
  promoted_bucket?: "primary" | "secondary" | null;
  last_dispatch_id?: string | null;
};

export const getPublicationWorkflow = (pub: ContentBriefPublication): PublicationWorkflow => {
  const snap = pub.context_snapshot;
  if (snap && typeof snap === "object" && !Array.isArray(snap)) {
    const wf = (snap as Record<string, Json | null>).workflow;
    if (wf && typeof wf === "object" && !Array.isArray(wf)) {
      const obj = wf as Record<string, Json | null>;
      const state = typeof obj.state === "string" ? (obj.state as PublicationWorkflowState) : "outbox";
      return {
        state,
        resolved_at: typeof obj.resolved_at === "string" ? obj.resolved_at : null,
        promoted_source_id: typeof obj.promoted_source_id === "string" ? obj.promoted_source_id : null,
        promoted_bucket:
          obj.promoted_bucket === "primary" || obj.promoted_bucket === "secondary"
            ? obj.promoted_bucket
            : null,
        last_dispatch_id: typeof obj.last_dispatch_id === "string" ? obj.last_dispatch_id : null,
      };
    }
  }
  return { state: "outbox" };
};

const writePublicationWorkflow = async (
  workspaceId: string,
  pub: ContentBriefPublication,
  patch: Partial<PublicationWorkflow> & { state: PublicationWorkflowState },
) => {
  const baseSnap =
    pub.context_snapshot && typeof pub.context_snapshot === "object" && !Array.isArray(pub.context_snapshot)
      ? { ...(pub.context_snapshot as Record<string, Json | null>) }
      : {};
  const current = getPublicationWorkflow(pub);
  const next: PublicationWorkflow = {
    ...current,
    ...patch,
    resolved_at: patch.state === "outbox" ? null : new Date().toISOString(),
  };
  baseSnap.workflow = next as unknown as Json;
  const { error } = await briefPublicationsTable()
    .update({ context_snapshot: baseSnap })
    .eq("id", pub.id)
    .eq("workspace_id", workspaceId);
  if (error) throw new Error(getFriendlyMessage(error, "Failed to update publication state"));
  return next;
};

export const archivePublication = (workspaceId: string, pub: ContentBriefPublication) =>
  writePublicationWorkflow(workspaceId, pub, { state: "archived" });

export const restorePublicationToOutbox = (workspaceId: string, pub: ContentBriefPublication) =>
  writePublicationWorkflow(workspaceId, pub, { state: "outbox" });

export const markPublicationSent = (
  workspaceId: string,
  pub: ContentBriefPublication,
  dispatchId: string,
) => writePublicationWorkflow(workspaceId, pub, { state: "sent", last_dispatch_id: dispatchId });

export const promotePublicationToSource = async (
  workspaceId: string,
  pub: ContentBriefPublication,
  bucket: "primary" | "secondary",
  briefTitle: string,
) => {
  const title = pub.prepared_title?.trim() || briefTitle || "Prepared output";
  const body = pub.prepared_body?.trim() || pub.prepared_excerpt?.trim() || "";
  const source = await createSource(workspaceId, {
    title,
    bucket,
    sourceType: "internal_publication",
    originLabel: `${briefTitle} · ${pub.destination_format}`,
    summary: pub.prepared_excerpt ?? undefined,
    notes: body || undefined,
  });
  await writePublicationWorkflow(workspaceId, pub, {
    state: "promoted",
    promoted_source_id: source.id,
    promoted_bucket: bucket,
  });
  return source;
};

import {
  parseBriefDocument,
  plainTextToBriefDocument,
  renderBriefDocument,
  type BriefDocument,
  type RenderContext,
  type RenderedBriefOutput,
} from "@/lib/briefDocument";

export const resolveDraftDocument = (
  draft: Pick<ContentBriefDraft, "revision_kind" | "draft_doc" | "draft_body"> | null | undefined,
): BriefDocument => {
  if (!draft) return { type: "doc", content: [] };

  // User-edit revisions: prefer the structured doc.
  if (draft.revision_kind === "user_edit" && draft.draft_doc) {
    const doc = draft.draft_doc as BriefDocument;
    if (doc && doc.type === "doc" && Array.isArray(doc.content)) return doc;
  }

  // AI rows (and legacy): draft_body may be plain text OR (rare) a stringified
  // BriefDocument from a future AI emitter. Try parse-as-doc first, fall back
  // to plain-text wrapping.
  const body = draft.draft_body ?? "";
  const parsed = parseBriefDocument(body);
  if (parsed) return parsed;
  return plainTextToBriefDocument(body);
};

/**
 * Render a brief draft to HTML + Markdown for publishing handoff.
 *
 * This is the single read-only contract Phase 4 publishing adapters will use.
 * It does not mutate the draft and does not write anywhere.
 */
export const getBriefRenderedOutput = (
  draft: Pick<ContentBriefDraft, "revision_kind" | "draft_doc" | "draft_body"> | null | undefined,
  ctx: RenderContext,
): RenderedBriefOutput => {
  const doc = resolveDraftDocument(draft);
  return renderBriefDocument(doc, ctx);
};
